import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguage } from "@/contexts/LanguageContext";
import { LENDER_TYPES, LOAN_PRODUCT_TYPES, COLLATERAL_OPTIONS } from "@/constants/lender-offer.constants";
import { Eye, FileText } from "lucide-react";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
 
interface OfferRow {
  id: string; offer_data: any; status: string; lender_company_name: string;
  loan_amount: number; interest_rate: number; loan_term_months: number;
  lender_type: string | null; loan_product_type: string | null; created_at: string;
}
 
function SectionHeader({ title }: { title: string }) {
  return <h3 className="text-lg font-bold text-primary border-b pb-2 mt-6 mb-3">{title}</h3>;
}
 
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '' || value === 0) return null;
  return <div className="flex justify-between py-1.5 border-b border-muted last:border-0"><span className="text-sm text-muted-foreground">{label}</span><span className="text-sm font-medium text-right max-w-[60%]">{value}</span></div>;
}
 
export function MyOffersSection({ userId }: { userId: string }) {
  const { language } = useLanguage();
  const [offers, setOffers] = useState<OfferRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState<OfferRow | null>(null);
 
  const lTypes = LENDER_TYPES[language as 'en' | 'fr'] || LENDER_TYPES.en;
  const pTypes = LOAN_PRODUCT_TYPES[language as 'en' | 'fr'] || LOAN_PRODUCT_TYPES.en;
  const cOpts = COLLATERAL_OPTIONS[language as 'en' | 'fr'] || COLLATERAL_OPTIONS.en;
 
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        // Remplace : supabase.from('lender_offers').select('*').eq('user_id', userId) → Firestore
        const snap = await getDocs(query(
          collection(db, "lenderOffers"),
          where("userId", "==", userId),
          orderBy("createdAt", "desc")
        ));
        setOffers(snap.docs.map(d => ({
          id: d.id,
          offer_data: d.data().offerData || d.data().offer_data || {},
          status: d.data().status || "submitted",
          lender_company_name: d.data().lenderCompanyName || d.data().lender_company_name || "",
          loan_amount: d.data().loanAmount || d.data().loan_amount || 0,
          interest_rate: d.data().interestRate || d.data().interest_rate || 0,
          loan_term_months: d.data().loanTermMonths || d.data().loan_term_months || 0,
          lender_type: d.data().lenderType || d.data().lender_type || null,
          loan_product_type: d.data().loanProductType || d.data().loan_product_type || null,
          created_at: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        })));
      } catch (err) { console.error("Error fetching offers:", err); }
      finally { setLoading(false); }
    };
    fetchOffers();
  }, [userId]);
 
  const statusBadge = (status: string) => {
    if (status === 'approved') return <Badge className="bg-green-500/20 text-green-600 border-green-500/50">Approved</Badge>;
    if (status === 'rejected') return <Badge className="bg-red-500/20 text-red-600 border-red-500/50">Rejected</Badge>;
    return <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/50">Submitted</Badge>;
  };
 
  if (loading) return null;
 
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />{language === 'fr' ? 'Mes Offres Soumises' : 'My Submitted Offers'}</CardTitle>
          <CardDescription>{offers.length} {language === 'fr' ? 'offre(s) soumise(s)' : 'offer(s) submitted'}</CardDescription>
        </CardHeader>
        <CardContent>
          {offers.length === 0 ? <p className="text-center py-6 text-muted-foreground">{language === 'fr' ? 'Aucune offre soumise pour le moment.' : 'No offers submitted yet.'}</p>
          : <div className="space-y-3">
            {offers.map(offer => (
              <div key={offer.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1"><span className="font-semibold">{offer.lender_company_name}</span>{statusBadge(offer.status)}</div>
                  <p className="text-sm text-muted-foreground">${Number(offer.loan_amount).toLocaleString()} — {offer.interest_rate}% — {offer.loan_term_months} {language === 'fr' ? 'mois' : 'months'}</p>
                  <p className="text-xs text-muted-foreground mt-1">{new Date(offer.created_at).toLocaleDateString()}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setSelectedOffer(offer)}><Eye className="h-4 w-4 mr-2" />{language === 'fr' ? 'Détails' : 'Details'}</Button>
              </div>
            ))}
          </div>}
        </CardContent>
      </Card>
      <Dialog open={!!selectedOffer} onOpenChange={() => setSelectedOffer(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader><DialogTitle>{selectedOffer?.lender_company_name} — ${Number(selectedOffer?.loan_amount || 0).toLocaleString()}</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            {selectedOffer && (() => {
              const d = selectedOffer.offer_data || {};
              return (<>
                <SectionHeader title={language === 'fr' ? "Type & Termes" : "Loan Type & Terms"} />
                <InfoRow label={language === 'fr' ? "Type de Prêteur" : "Lender Type"} value={lTypes[d.lenderType as keyof typeof lTypes] || d.lenderType} />
                <SectionHeader title={language === 'fr' ? "Termes Commerciaux" : "Commercial Terms"} />
                <InfoRow label={language === 'fr' ? "Montant" : "Amount"} value={`$${Number(d.loanAmountOffered || 0).toLocaleString()}`} />
                <InfoRow label={language === 'fr' ? "Durée" : "Term"} value={`${d.loanTermLengthMonths} ${language === 'fr' ? 'mois' : 'months'}`} />
                <InfoRow label={language === 'fr' ? "Taux" : "Rate"} value={`${d.interestRateValue}%`} />
              </>);
            })()}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}